import type { FormTabSelectFlatValueItem } from '@ricoh/ria.client.core/components/form/tab-select/content/_.type';
import type { PatternFormFieldItemConstant } from '@ricoh/ria.client.core/types/component/pattern.type';

import { ClientError } from '@ricoh/ria.client.core/errors';
import { isType } from '@ricoh/ria.client.core/utils/data/object';

import type { PatternAccessScope } from '~/common/logic/feature/settings/batch-type-library/pattern';
import type { FieldTransformRule } from '~/common/model/api/batch-type';
import type { DataType } from '~/common/model/api/common';
import type { FieldTransformRuleFormField, TransformationFieldSubmitData } from '~/components/feature/settings/batch-type-library/form/transformation-field';

import batchTypeLogic from '~/common/logic/feature/settings/batch-type-library/batch-type';

/**
 * Create Transform Patterns FormField.
 *
 * @param {FieldTransformRule[]} fieldTransformationRules TransformPatterns.
 *
 * @returns {FormTabSelectFlatValueItem[]} Transform Patterns FormField.
 */
export function createFieldTransformationFormField(fieldTransformationRules?: FieldTransformRule[]): TransformationFieldSubmitData {
  if (!fieldTransformationRules || fieldTransformationRules.length <= 0) {
    return {
      rules: [],
    };
  }

  return {
    rules: fieldTransformationRules.map(({ businessRuleName, pattern, parameters }) => {
      const [businessRuleFormFieldItem] = batchTypeLogic.businessRule.createBusinessRuleFormFieldItem(pattern);
      if (isType<PatternFormFieldItemConstant>(businessRuleFormFieldItem, 'const')) {
        return {
          rule: {
            tabKey: 'batchBusinessRule',
            item: '',
          },
        };
      }

      const parametersPatternFormFields = parameters
        ? Object.entries(parameters).reduce<FieldTransformRuleFormField['parameters']>((prev, [key, parameter]) => {
            if (!prev) {
              return prev;
            }

            prev[key] = batchTypeLogic.businessRule.createBusinessRuleFormFieldItem(parameter);
            return prev;
          }, {})
        : undefined;

      return {
        rule: {
          tabKey: businessRuleFormFieldItem?.tabKey,
          item: businessRuleName,
        } as FormTabSelectFlatValueItem,
        parameters: parametersPatternFormFields,
      };
    }),
  };
}

/**
 * Create Field Transformation.
 *
 * @param {DataType} type Type.
 * @param {FieldTransformRuleFormField[]} rules Rules.
 * @param {PatternAccessScope} scope Scope for the transformation patterns.
 *
 * @returns {FieldTransformRule[]} FieldTransformationRules.
 */
export function createFieldTransformation(type: DataType, rules: FieldTransformRuleFormField[], scope: PatternAccessScope): FieldTransformRule[] {
  return rules?.map<FieldTransformRule>(({ rule, parameters }) => {
    if (!rule) {
      throw new ClientError({
        module: 'logic',
        message: 'Invalid field transformation rule',
        cause: rule,
      });
    }

    const businessRuleName = rule.item as string;

    const pattern = batchTypeLogic.businessRule.createBusinessRulePattern(
      [
        {
          $is: 'tag',
          tabKey: rule.tabKey as string,
          value: rule.item,
        },
      ],
      scope,
    );

    const parametersPatterns = parameters
      ? Object.entries(parameters).reduce<FieldTransformRule['parameters']>((prev, [key, parameter]) => {
          if (!prev) {
            return prev;
          }

          prev[key] = batchTypeLogic.businessRule.createBusinessRulePattern(parameter, 'batch');
          return prev;
        }, {})
      : undefined;

    return {
      businessRuleName,
      pattern,
      targetType: type,
      parameters: parametersPatterns,
    };
  });
}
